<template>
  <div class="app-shell home-page info-page profile-page">
    <div class="app-shell__main">
      <AppHeader />

      <main class="info-main">
        <section class="info-hero info-hero--profile">
          <div class="profile-hero-top">
            <div class="profile-hero-user">
              <img v-if="userInfo.avatar" :src="userInfo.avatar" class="profile-hero-avatar" />
              <div v-else class="profile-hero-avatar profile-hero-avatar--placeholder">A</div>
              <div class="profile-hero-copy">
                <div class="info-badge">Account Center</div>
                <h1 class="info-title">个人中心</h1>
                <p class="info-description">集中查看账号状态、剩余次数、会员权益与常用帮助入口，后续功能也会统一收在这里。</p>
              </div>
            </div>
            <button type="button" class="primary-button profile-hero-action" @click="goToWebPay">会员中心</button>
          </div>

          <div class="info-summary profile-summary">
            <article class="info-summary-card">
              <strong>{{ formattedNickname }}</strong>
              <span>当前账号</span>
            </article>
            <article class="info-summary-card">
              <strong>{{ userInfo.member ? '尊贵会员' : '普通用户' }}</strong>
              <span>{{ userInfo.member ? `到期 ${formattedDeadlineTime}` : '尚未开通会员' }}</span>
            </article>
            <article class="info-summary-card">
              <strong>{{ userInfo.times || 0 }} 次</strong>
              <span>剩余可用次数</span>
            </article>
          </div>
        </section>

        <section class="info-panel">
          <div class="info-section-head">
            <h2 class="section-title">账号概览</h2>
            <p>将常用信息和操作集中在一个页面里，避免都挤在顶部弹窗里。</p>
          </div>

          <div class="profile-overview-grid">
            <article class="info-card">
              <div class="info-card__head">
                <div class="info-card__index">01</div>
                <h3 class="info-card__title">账号信息</h3>
              </div>
              <div class="profile-meta-list">
                <div class="profile-meta-row">
                  <span>账号 ID</span>
                  <strong>{{ userIdText }}</strong>
                </div>
                <div class="profile-meta-row">
                  <span>昵称</span>
                  <strong>{{ userInfo.nickname || '--' }}</strong>
                </div>
                <div class="profile-meta-row">
                  <span>会员状态</span>
                  <strong :class="{ 'profile-meta-row__vip': userInfo.member }">{{ userInfo.member ? '尊贵会员' : '普通用户' }}</strong>
                </div>
                <div class="profile-meta-row" v-if="userInfo.member">
                  <span>到期时间</span>
                  <strong>{{ formattedDeadlineTime }}</strong>
                </div>
              </div>
            </article>

            <article class="info-card">
              <div class="info-card__head">
                <div class="info-card__index">02</div>
                <h3 class="info-card__title">使用情况</h3>
              </div>
              <div class="profile-usage-card">
                <div class="profile-usage-card__value">{{ userInfo.times || 0 }}</div>
                <div class="profile-usage-card__label">当前剩余次数</div>
                <p class="profile-usage-card__text">如果需要更多次数或更稳定的使用权益，可以直接前往会员中心开通会员或充值。</p>
              </div>
            </article>
          </div>
        </section>

        <section class="info-panel">
          <div class="info-section-head">
            <h2 class="section-title">常用入口</h2>
            <p>和账号相关、使用中最常会点到的页面，都统一放在这里。</p>
          </div>

          <div class="profile-entry-grid">
            <button type="button" class="profile-entry-card" @click="goToCurrencyRecord">
              <span class="profile-entry-card__tag">Record</span>
              <strong>积分明细</strong>
              <p>查看最近一个月积分增加与消费记录。</p>
            </button>
            <button type="button" class="profile-entry-card" @click="goToWebPay">
              <span class="profile-entry-card__tag">Member</span>
              <strong>会员中心</strong>
              <p>选择会员套餐或按需充值次数。</p>
            </button>
            <button type="button" class="profile-entry-card" @click="goToCommonQuestion">
              <span class="profile-entry-card__tag">FAQ</span>
              <strong>常见问题</strong>
              <p>快速查看解析、保存、积分与使用限制说明。</p>
            </button>
            <button type="button" class="profile-entry-card" @click="goToTutorial">
              <span class="profile-entry-card__tag">Guide</span>
              <strong>使用教程</strong>
              <p>用最短路径了解网站主要使用流程。</p>
            </button>
            <button type="button" class="profile-entry-card" @click="goToServiceStatement">
              <span class="profile-entry-card__tag">Policy</span>
              <strong>服务声明</strong>
              <p>查看服务范围、使用边界与责任说明。</p>
            </button>
          </div>
        </section>
      </main>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { getUserInfo } from '@/api/user'
import { formatTime } from '@/utils/date'

export default {
  name: 'ProfileCenter',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      userInfo: {}
    }
  },
  computed: {
    formattedNickname() {
      return this.userInfo.nickname || '未登录用户'
    },
    userIdText() {
      if (!this.userInfo.id && this.userInfo.id !== 0) return '--'
      return `SYF${String(this.userInfo.id).padStart(6, '0')}`
    },
    formattedDeadlineTime() {
      const deadlineTime = this.userInfo.deadlineTime
      if (!deadlineTime) return '--'
      const date = typeof deadlineTime === 'number' || /^\d+$/.test(String(deadlineTime))
        ? new Date(Number(deadlineTime))
        : new Date(deadlineTime)
      return Number.isNaN(date.getTime()) ? String(deadlineTime) : formatTime(date)
    }
  },
  mounted() {
    this.fetchUserInfo()
  },
  methods: {
    async fetchUserInfo() {
      try {
        const info = await getUserInfo()
        this.userInfo = info || {}
      } catch (error) {
        console.error(error)
        this.userInfo = {}
      }
    },
    goToWebPay() {
      this.$router.push('/web-pay')
    },
    goToCurrencyRecord() {
      this.$router.push('/currency-record')
    },
    goToCommonQuestion() {
      this.$router.push('/common-question')
    },
    goToTutorial() {
      this.$router.push('/tutorial')
    },
    goToServiceStatement() {
      this.$router.push('/service-statement')
    }
  }
}
</script>
